import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DownloadIcon from '@mui/icons-material/Download';
import * as XLSX from 'xlsx';
import { folyamatlapService, FolyamatlapTetelDto } from '../services/folyamatlapService';
import { permissionService, Permissions } from '../services/permissionService';

interface FolyamatlapContentProps {
  munkaszam?: string | null;
  onBack?: () => void;
}

const formatDate = (value: string): string => {
  if (!value) return '-';

  const date = new Date(value);
  if (isNaN(date.getTime())) {
    return value;
  }

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');

  return `${year}/${month}/${day}`;
};

const FolyamatlapContent: React.FC<FolyamatlapContentProps> = ({ munkaszam, onBack }) => {
  const [items, setItems] = useState<FolyamatlapTetelDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [canExport, setCanExport] = useState(
    permissionService.hasPermission(Permissions.ExportFolyamatlap)
  );

  useEffect(() => {
    setCanExport(permissionService.hasPermission(Permissions.ExportFolyamatlap));

    const unsubscribe = permissionService.onPermissionsChanged(() => {
      setCanExport(permissionService.hasPermission(Permissions.ExportFolyamatlap));
    });

    return unsubscribe;
  }, []);

  useEffect(() => {
    const loadData = async () => {
      if (!munkaszam) {
        setItems([]);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const data = await folyamatlapService.getByMunkaszam(munkaszam);
        const sorted = [...data].sort(
          (a, b) => new Date(b.datum).getTime() - new Date(a.datum).getTime()
        );
        setItems(sorted);
      } catch (err) {
        console.error('Hiba a folyamatlap betöltése során:', err);
        setError('Nem sikerült betölteni a folyamatlap adatait.');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [munkaszam]);

  const exportRows = useMemo(
    () =>
      items.map((item) => ({
        Dátum: formatDate(item.datum),
        Típus: item.tipus,
        'Iktatás iránya': item.iktatasIranya || '-',
        Tárgy: item.targy || '-',
        Név: item.nev || '-',
        'Időráfordítás (óra)': item.idoraforditasOra ?? '-',
      })),
    [items]
  );

  const handleExport = () => {
    if (!munkaszam || exportRows.length === 0) {
      return;
    }

    const worksheet = XLSX.utils.json_to_sheet(exportRows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Folyamatlap');

    const safeMunkaszam = munkaszam.replace(/[\\/:*?"<>|]/g, '_');
    XLSX.writeFile(workbook, `folyamatlap_${safeMunkaszam}.xlsx`);
  };

  return (
    <Box sx={{ flexGrow: 1, minWidth: 0 }}>
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Box>
              <Typography variant="h5">Folyamatlap</Typography>
              <Typography variant="body2" color="text.secondary">
                Munkaszám: {munkaszam || '-'}
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="outlined"
                startIcon={<ArrowBackIcon />}
                onClick={onBack}
              >
                Vissza a munkaszámokhoz
              </Button>
              {canExport && (
                <Button
                  variant="contained"
                  startIcon={<DownloadIcon />}
                  onClick={handleExport}
                  disabled={items.length === 0}
                >
                  Export Excel
                </Button>
              )}
            </Box>
          </Box>

          {loading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          )}

          {!loading && error && <Alert severity="error">{error}</Alert>}

          {!loading && !error && (
            <>
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold' }}>Dátum</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Típus</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Iktatás iránya</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Tárgy</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Név</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Időráfordítás (óra)</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {items.map((item, index) => (
                      <TableRow key={`${item.tipus}-${item.datum}-${index}`}>
                        <TableCell>{formatDate(item.datum)}</TableCell>
                        <TableCell>{item.tipus}</TableCell>
                        <TableCell>{item.iktatasIranya || '-'}</TableCell>
                        <TableCell>{item.targy || '-'}</TableCell>
                        <TableCell>{item.nev || '-'}</TableCell>
                        <TableCell>{item.idoraforditasOra ?? '-'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {items.length === 0 && (
                <Typography variant="body1" sx={{ mt: 2 }} color="text.secondary">
                  Nincsenek adatok ehhez a munkaszámhoz.
                </Typography>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default FolyamatlapContent;
